import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowUpDown, ArrowUp, ArrowDown, Filter, Sparkles } from 'lucide-react';

export const OPERATION_STATUSES = ['Aranacak', 'Takipte', 'Teklif verildi', 'Ertelendi', 'Yenilemeyecek'];
export const RENEWAL_STATUSES = ['Yenileme yok', 'Kısmi yenilendi', 'Tam yenilendi'];
export const COLLECTION_STATUSES = ['Tahsilat yok', 'Kısmi tahsilat', 'Tam tahsilat'];

export const collectionStatusOf = (c) => {
  if (!c.total_renewed_amount || !c.total_collected) return 'Tahsilat yok';
  return c.remaining_balance > 0 ? 'Kısmi tahsilat' : 'Tam tahsilat';
};

const isOverdue = (c) => c.operation_status !== 'Yenilemeyecek' && c.renewal_status !== 'Tam yenilendi'
  && c.next_followup_date && new Date(c.next_followup_date) < new Date();

const smartRank = (c) => {
  if (isOverdue(c)) return 0;
  if (c.operation_status === 'Yenilemeyecek' || c.renewal_status === 'Tam yenilendi') return 3;
  return c.next_followup_date ? 1 : 2;
};

const OP_ORDER = ['Aranacak', 'Takipte', 'Teklif verildi', 'Ertelendi', 'Yenilemeyecek'];
const RN_ORDER = ['Yenileme yok', 'Kısmi yenilendi', 'Tam yenilendi'];

const VALUE_OF = {
  name: (c) => c.customer_name,
  operation: (c) => OP_ORDER.indexOf(c.operation_status),
  renewal: (c) => RN_ORDER.indexOf(c.renewal_status),
  devices: (c) => c.source_device_count,
  renewed: (c) => c.total_renewed_amount,
  collected: (c) => c.total_collected,
  balance: (c) => c.remaining_balance
};

const smartSort = (a, b) => smartRank(a) - smartRank(b)
  || (a.next_followup_date || '9999').localeCompare(b.next_followup_date || '9999')
  || b.source_device_count - a.source_device_count;

const makeSorter = ({ key, dir }) => {
  if (key === 'smart' || !VALUE_OF[key]) return smartSort;
  const get = VALUE_OF[key];
  const sign = dir === 'asc' ? 1 : -1;
  return (a, b) => {
    const va = get(a), vb = get(b);
    const cmp = typeof va === 'string' ? va.localeCompare(vb, 'tr') : va - vb;
    return sign * cmp || smartSort(a, b);
  };
};

export const applyCaseFilters = (cases, { renewalFilter, collectionFilter, sort }) =>
  cases
    .filter(c => renewalFilter === 'all' || c.renewal_status === renewalFilter)
    .filter(c => collectionFilter === 'all' || collectionStatusOf(c) === collectionFilter)
    .sort(makeSorter(sort));

const FilterSelect = ({ value, onChange, allLabel, options, testId, width = 'md:w-44' }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`w-full ${width}`} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{allLabel}</SelectItem>
      {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export function CaseListToolbar({ statusFilter, setStatusFilter, renewalFilter, setRenewalFilter, collectionFilter, setCollectionFilter, count }) {
  return (
    <div className="bg-white border rounded-lg p-3 mb-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid="case-list-toolbar">
      <div className="flex items-center gap-2 text-sm text-gray-500 shrink-0"><Filter className="w-4 h-4" />Filtre</div>
      <FilterSelect value={statusFilter} onChange={setStatusFilter} allLabel="Operasyon: Tümü" options={OPERATION_STATUSES} testId="filter-operation-status" width="md:w-52" />
      <FilterSelect value={renewalFilter} onChange={setRenewalFilter} allLabel="Yenileme: Tümü" options={RENEWAL_STATUSES} testId="filter-renewal-status" width="md:w-52" />
      <FilterSelect value={collectionFilter} onChange={setCollectionFilter} allLabel="Tahsilat: Tümü" options={COLLECTION_STATUSES} testId="filter-collection-status" width="md:w-52" />
      <span className="text-xs text-gray-400 whitespace-nowrap lg:ml-auto" data-testid="case-list-count">{count} dosya</span>
    </div>
  );
}

export const CASE_COLS = { status: 'md:w-64', num: 'w-20 md:w-24' };

function SortHeader({ label, sortKey, sort, setSort, className = '', align = 'right' }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'desc' ? ArrowDown : ArrowUp;
  const next = () => {
    if (!active) return setSort({ key: sortKey, dir: 'desc' });
    if (sort.dir === 'desc') return setSort({ key: sortKey, dir: 'asc' });
    setSort({ key: 'smart', dir: 'desc' });
  };
  return (
    <button type="button" onClick={next} data-testid={`sort-${sortKey}`}
      title={active ? (sort.dir === 'desc' ? 'Çoktan aza' : 'Azdan çoğa') : 'Sırala'}
      className={`flex items-center gap-1 text-xs hover:text-gray-800 transition-colors ${align === 'right' ? 'justify-end' : ''} ${active ? 'text-blue-600 font-semibold' : 'text-gray-500'} ${className}`}>
      {label}<Icon className="w-3.5 h-3.5" />
    </button>
  );
}

export function CaseListHeader({ sort, setSort }) {
  const p = { sort, setSort };
  return (
    <div className="hidden md:flex items-center gap-4 px-4 py-2 mb-2 bg-white border rounded-lg" data-testid="case-list-header">
      <div className="flex-1 flex items-center gap-3">
        <SortHeader label="Müşteri" sortKey="name" align="left" {...p} />
        <button type="button" onClick={() => setSort({ key: 'smart', dir: 'desc' })} data-testid="sort-smart"
          className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors ${sort.key === 'smart' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
          <Sparkles className="w-3 h-3" />Akıllı sıralama
        </button>
      </div>
      <div className={`flex gap-2 ${CASE_COLS.status}`}>
        <SortHeader label="Durum" sortKey="operation" align="left" {...p} />
        <SortHeader label="Yenileme" sortKey="renewal" align="left" {...p} />
      </div>
      <div className="flex gap-6">
        <SortHeader label="Cihaz" sortKey="devices" className={CASE_COLS.num} {...p} />
        <SortHeader label="Gerçekleşen" sortKey="renewed" className={CASE_COLS.num} {...p} />
        <SortHeader label="Tahsilat" sortKey="collected" className={CASE_COLS.num} {...p} />
        <SortHeader label="Bakiye" sortKey="balance" className={CASE_COLS.num} {...p} />
      </div>
    </div>
  );
}
